/** Pure helpers behind EmitEventDialog: the draft, what blocks a submit, the wire
 *  params, and the labels the dialog and the report share. */
import type { BatchSelection } from '@/api/batch';
import type {
  EmitEventCatalog,
  EmitEventCatalogEntry,
  EmitEventContext,
  EmitEventFamily,
  EmitEventParams,
  EmitEventRecordContext,
  EmitEventSource,
} from '@/api/emitEvents';

export interface EmitDraft {
  code: string | null;
  tag: string;
  reason: string;
  source: EmitEventSource;
  /** Borrow: EDL status values; empty = any. */
  statuses: number[];
  /** Borrow: emitted within [after, before]; either bound may be open. */
  window: [Date | null, Date | null];
  /** Borrow (diff events): pass over candidates with an empty diff. */
  skipEmpty: boolean;
  jsonText: string;
  webhooks: boolean;
  force: boolean;
}

export const EMPTY_DRAFT: EmitDraft = {
  code: null,
  tag: '',
  reason: '',
  source: 'empty',
  statuses: [],
  window: [null, null],
  skipEmpty: true,
  jsonText: '',
  webhooks: false,
  force: false,
};

export function findEntry(catalog: EmitEventCatalog | undefined, code: string | null): EmitEventCatalogEntry | null {
  if (!catalog || !code) return null;
  return catalog.events.find((e) => e.code === code) ?? null;
}

const PHASE_LABELS: Record<string, string> = {
  postbooking: 'after booking',
  pretrip: 'before the trip',
  ontrip: 'during the trip',
  endtrip: 'before the trip ends',
  posttrip: 'after the trip',
};

/** Autocomplete group of an entry (catalog order keeps groups contiguous). */
export function groupLabel(entry: EmitEventCatalogEntry): string {
  if (entry.kind === 'lifecycle') return entry.family === 'order' ? 'Order lifecycle' : 'Booking lifecycle';
  if (entry.kind === 'update') return 'Update';
  return `Timed — ${PHASE_LABELS[entry.phase] ?? entry.phase}`;
}

/** Record noun per family, for dialog and report copy. */
export function nounFor(family: EmitEventFamily): 'booking' | 'order' {
  return family === 'order' ? 'order' : 'booking';
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** A starting point for the JSON source: an editable example diff, or the entry's
 *  parameters with example values. */
export function jsonTemplate(entry: EmitEventCatalogEntry): string {
  if (entry.context === 'diff') {
    const field = entry.family === 'order' ? 'order.contact.email' : 'participant.tag_context.pickup';
    const [oldValue, newValue] = entry.family === 'order' ? ['old@example.com', 'new@example.com'] : ['08:00', '08:30'];
    return JSON.stringify({ fields: [{ field, old_value: oldValue, new_value: newValue }] }, null, 2);
  }
  const obj: Record<string, unknown> = {};
  for (const p of entry.params) {
    if (p.type === 'date') obj[p.name] = todayIso();
    else if (p.type === 'time') obj[p.name] = p.nullable ? null : '09:00';
    else if (p.type === 'bool') obj[p.name] = false;
    else obj[p.name] = p.choices[0] ?? 0;
  }
  return JSON.stringify(obj, null, 2);
}

export function parseJsonObject(text: string): { value?: Record<string, unknown>; error?: string } {
  if (!text.trim()) return { error: 'Enter a JSON object.' };
  try {
    const v: unknown = JSON.parse(text);
    if (v === null || typeof v !== 'object' || Array.isArray(v)) return { error: 'Must be a JSON object.' };
    return { value: v as Record<string, unknown> };
  } catch (e) {
    return { error: (e as Error).message };
  }
}

// eslint-disable-next-line no-control-regex -- the point is to detect them
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/;

export function hasControlChars(s: string): boolean {
  return CONTROL_CHARS.test(s);
}

/** Why the dialog can't dispatch yet (tooltip + disabled buttons), or null. */
export function submitBlocker(catalog: EmitEventCatalog | undefined, draft: EmitDraft): string | null {
  if (!catalog) return 'Loading…';
  if (!catalog.enabled) return 'Emitting events is disabled in this environment.';
  const entry = findEntry(catalog, draft.code);
  if (!entry) return 'Pick an event.';
  const tag = draft.tag.trim();
  if (!tag) return 'Enter a tag.';
  if (tag.length > catalog.tag.max_user_length) return `The tag must be at most ${catalog.tag.max_user_length} characters.`;
  if (hasControlChars(tag)) return 'The tag must not contain control characters.';
  if (entry.context !== 'none') {
    if (draft.source === 'json') {
      const { error } = parseJsonObject(draft.jsonText);
      if (error) return `Fix the JSON: ${error}`;
    }
    if (draft.source === 'borrow') {
      const [after, before] = draft.window;
      if (after && before && after >= before) return 'The window’s start must be before its end.';
    }
  }
  if (draft.reason.trim().length < catalog.min_reason) return `Give a reason of at least ${catalog.min_reason} characters.`;
  return null;
}

function contextFor(entry: EmitEventCatalogEntry, draft: EmitDraft): EmitEventContext | null {
  if (entry.context === 'none') return null;
  if (draft.source === 'json') return { source: 'json', json: parseJsonObject(draft.jsonText).value ?? {} };
  if (draft.source === 'borrow') {
    return {
      source: 'borrow',
      borrow: {
        statuses: [...draft.statuses].sort((a, b) => a - b),
        after: draft.window[0]?.toISOString() ?? null,
        before: draft.window[1]?.toISOString() ?? null,
        ...(entry.context === 'diff' ? { skip_empty: draft.skipEmpty } : {}),
      },
    };
  }
  return { source: 'empty' };
}

/** Wire params. Webhooks/force are only sent when the entry supports them. */
export function buildParams(catalog: EmitEventCatalog, draft: EmitDraft, dryRun: boolean): EmitEventParams {
  const entry = findEntry(catalog, draft.code);
  if (!entry) throw new Error('No event picked');
  return {
    event: entry.code,
    tag: draft.tag.trim(),
    reason: draft.reason.trim(),
    dry_run: dryRun,
    webhooks: entry.fires_webhooks && draft.webhooks,
    force: entry.gates.length > 0 && draft.force,
    context: contextFor(entry, draft),
  };
}

/** What a dry run was run for: editing anything that changes the params (other
 *  than the reason) or the selection voids its review. */
export function operationIdentity(
  catalog: EmitEventCatalog | undefined,
  draft: EmitDraft,
  selection: BatchSelection,
): string {
  if (!catalog || !findEntry(catalog, draft.code)) return '';
  const p = buildParams(catalog, draft, true);
  return JSON.stringify({ event: p.event, tag: p.tag, webhooks: p.webhooks, force: p.force, context: p.context, selection });
}

/** One-line description of where a record's diff/params came from. */
export function contextSummary(ctx?: EmitEventRecordContext | null): string {
  if (!ctx) return 'current state';
  let head: string = ctx.source ?? '—';
  if (ctx.source === 'borrow') {
    const when = ctx.created_ts ? new Date(ctx.created_ts).toISOString().slice(0, 16).replace('T', ' ') : '?';
    head = `borrowed from ${when} UTC (${ctx.status_label ?? ctx.status ?? '?'})`;
    const skipped = Object.entries(ctx.skipped ?? {})
      .filter(([, n]) => n > 0)
      .map(([k, n]) => `${n} ${k.replace('_', ' ')}`);
    if (skipped.length) head += `, skipped ${skipped.join(', ')}`;
  } else if (ctx.source === 'empty') {
    head = ctx.params ? 'defaults' : 'empty diff';
  } else if (ctx.source === 'json') {
    head = 'pasted';
  }
  if (ctx.affected_fields) {
    const n = ctx.affected_fields.length;
    return `${head} · ${n} field${n === 1 ? '' : 's'}${n ? `: ${ctx.affected_fields.slice(0, 3).join(', ')}${n > 3 ? '…' : ''}` : ''}`;
  }
  if (ctx.params) return `${head} · ${JSON.stringify(ctx.params)}`;
  return head;
}
